import React, { useState, useEffect, useRef, useCallback } from 'react';
import { makeStyles } from '@material-ui/styles';

import { SearchToolbar, SearchTable } from './components';
import { search } from 'api/search/core';

import {
  CAR_TAB_RESPONSE_STATUSES,
  FIRE_AND_INJURY_TAB_RESPONSE_STATUSES,
} from 'const';

import queryString from "query-string";
import { NECESSARY_CHANGE_TYPES_MAP, ALLOWED_CHANGE_TYPES_MAP } from "const";
import { useLocation } from "react-router-dom";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  content: {
    marginTop: theme.spacing(2),
  },
}));

const defaultQuery = {
  pageSize: 200,
  page: 0,
  orderBy: {
    field: 'id'
  },
  orderDirection: 'desc'
}

const Search = (props) => {
  const location = useLocation();
  const { searchTabType } = props;
  const classes = useStyles();

  const [applies, setApplies] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // 検索条件の入力値を変更して、検索ボタンを押さずにページングした際などは、
  // フォームの入力値ではなく、前回検索ボタンを押下したときの検索条件でGETするので、
  // 検索時に送る条件と入力フィールドの値を別の変数で管理している
  // 入力フィールドの値を管理する変数
  const applyIdRef = useRef(null);

  // スペース区切りの文字列になっている複数のapplyIDを、配列にして返す
  // 空文字またはスペースのみが渡ってきた場合は、空配列を返す
  const splitApplyIds = (values) => {
    const result = values
      .replace(/　/g, ' ') // 全角スペースを半角スペースに変換
      .trim() // 両端の全角空白と半角空白を削除
      .replace(/\s+/g, ' ') // 連続した半角スペースを一つの半角スペースに変換
      .split(/\s/); // 半角スペースで区切って配列にして返す
    return result.length === 1 && !result[0] ? [] : result;
  };

  const [policyNumber, setPolicyNumber] = useState(null);
  const [name, setName] = useState(null);
  const [nameKana, setNameKana] = useState(null);
  const [createdAtFrom, setCreatedAtFrom] = useState(null);
  const [createdAtTo, setCreatedAtTo] = useState(null);

  const defaultResponsesStatuses = useCallback(() => {
    if (searchTabType === 'car') {
      return CAR_TAB_RESPONSE_STATUSES.filter(status => status !== '9')
    } else if (searchTabType === 'fireAndInjury') {
      return FIRE_AND_INJURY_TAB_RESPONSE_STATUSES.filter(status => status !== '9')
    } else {
      return []
    }
  }, [searchTabType])

  const generateNecessaryChangeTypes = useCallback((filterBy) => {
    return NECESSARY_CHANGE_TYPES_MAP[searchTabType][filterBy] ? NECESSARY_CHANGE_TYPES_MAP[searchTabType][filterBy] : []
  }, [searchTabType])

  const generateAllowedChangeTypes = useCallback((filterBy) => {
    return ALLOWED_CHANGE_TYPES_MAP[searchTabType][filterBy] ? ALLOWED_CHANGE_TYPES_MAP[searchTabType][filterBy] : []
  }, [searchTabType])

  const defaultNecessaryChangeTypes = () => {
    const queryParams = queryString.parse(window.location.search);
    const filterBy = queryParams.filterBy;
    return generateNecessaryChangeTypes(filterBy)
  }
  const defaultAllowedChangeTypes = () => {
    const queryParams = queryString.parse(window.location.search);
    const filterBy = queryParams.filterBy;
    return generateAllowedChangeTypes(filterBy)
  }
  const [responseStatuses, setResponseStatuses] = useState(defaultResponsesStatuses);
  const [necessaryChangeTypes, setNecessaryChangeTypes] = useState(defaultNecessaryChangeTypes);
  const [allowedChangeTypes, setAllowedChangeTypes] = useState(defaultAllowedChangeTypes);
  const [isIdentifiedAll, setIsIdentifiedAll] = useState("0");
  const [feeCategory, setFeeCategory] = useState(null);
  const [cancellationCategory, setCancellationCategory] = useState(null);
  const [existsCallDesignated, setExistsCallDesignated] = useState(false);
  const [isSmsSendingRequired, setIsSmsSendingRequired] = useState(false);
  const [lineChatAvailability, setLineChatAvailability] = useState(null);

  const [query, setQuery] = useState(defaultQuery);

  // 検索トリガーフラグ
  const [shouldSearchTriggered, setShouldSearchTriggered] = useState(false);

  const dateFormat = (date) =>
    `${date.getFullYear()}-${('0' + String(date.getMonth() + 1)).slice(-2)}-${(
      '0' + String(date.getDate())
    ).slice(-2)}T${('0' + String(date.getHours())).slice(-2)}:${(
      '0' + String(date.getMinutes())
    ).slice(-2)}`;

    /**
     * 実際に一覧を検索するために利用する検索条件（画面上の設定条件とは別）
     * 例えば画面上の条件を変更しても検索ボタン押下しない限りは設定が反映されない。
     */
    const [condition, setCondition] = useState()

  /**
   * 検索ボタン押下時処理。
   * クエリと検索条件の変更を反映し、検索をトリガーする。
   */
  const onClickSearch = () => {
    setQuery({
      ...query,
      page: 0,
    });
    setShouldSearchTriggered(true)
    setCondition({
      policyNumber: policyNumber || null,
      name: name || null,
      nameKana: nameKana || null,
      applyIds: splitApplyIds(applyIdRef.current.value),
      createdAtFromByValue: createdAtFrom ? dateFormat(createdAtFrom) : null,
      createdAtToByValue: createdAtTo ? dateFormat(createdAtTo) : null,
      searchTabType: searchTabType,
      responseStatuses: responseStatuses,
      necessaryChangeTypes: necessaryChangeTypes,
      allowedChangeTypes: necessaryChangeTypes.length === 0 ? [] : allowedChangeTypes,
      isIdentifiedAll: String(isIdentifiedAll ?? 0),
      feeCategory: String(feeCategory ?? ""),
      cancellationCategory: String(cancellationCategory ?? ""),
      existsCallDesignated: String(existsCallDesignated),
      isSmsSendingRequired: String(isSmsSendingRequired),
      lineChatAvailability: lineChatAvailability,
    })
  };

  const onClickDetail = (apply) => {
    // noopener,noreferrerの付け方の参考元: https://python5.com/q/mxagomya
    const newWindow = window.open(`/detail?apply_id=${apply.apply_id}`, '_blank', 'noopener,noreferrer')
    if (newWindow) newWindow.opener = null
    return true;
  };

  // locationの変更に応じて検索条件を設定し検索をトリガーする
  useEffect(() => {
    const { filterBy } = queryString.parse(location.search);
    const newNecessaryChangeType = generateNecessaryChangeTypes(filterBy);
    const newAllowedChangeType = generateAllowedChangeTypes(filterBy);
    setQuery(defaultQuery)

    setNecessaryChangeTypes(newNecessaryChangeType)
    setAllowedChangeTypes(newAllowedChangeType)
    const defaultResponseStatuses = defaultResponsesStatuses()
    setResponseStatuses(defaultResponseStatuses)

    applyIdRef.current.value = null
    setPolicyNumber(null)
    setName(null)
    setNameKana(null)
    setCreatedAtFrom(null)
    setCreatedAtTo(null)
    setIsIdentifiedAll(null)
    setFeeCategory(null)
    setCancellationCategory(null)
    setExistsCallDesignated(false)
    setIsSmsSendingRequired(false)
    setLineChatAvailability(null)

    setCondition({
      policyNumber: null,
      name: null,
      nameKana: null,
      applyIds: splitApplyIds(applyIdRef.current.value),
      createdAtFromByValue: null,
      createdAtToByValue: null,
      searchTabType: searchTabType,
      responseStatuses: defaultResponseStatuses,
      necessaryChangeTypes: newNecessaryChangeType,
      allowedChangeTypes: newAllowedChangeType,
      isIdentifiedAll: String(0),
      feeCategory:  null,
      cancellationCategory:  null,
      existsCallDesignated: String(false),
      isSmsSendingRequired: String(false),
      lineChatAvailability: null,
    })
    setShouldSearchTriggered(true)
  }, [
    location,
    generateAllowedChangeTypes,
    generateNecessaryChangeTypes,
    defaultResponsesStatuses,
    searchTabType
  ]);

  /**
   * 検索トリガーフラグに応じて一覧データを取得する。
   */
  useEffect(() => {
    if (!shouldSearchTriggered) return;
    const getData = async () => {
      const re = /^[ァ-ヶｦ-ﾟ]([ァ-ヶｦ-ﾟー\-‐‑–—―−ｰ－\s])*$/
      if (condition.nameKana && !re.test(condition.nameKana)) {
        alert('契約者名（カナ）にはカタカナを入力してください。')
        return
      }
      setLoading(true);

      const result = await search(condition, query);
      setApplies(result.record);
      setTotalCount(result.count);
      setLoading(false);
    };
    getData();
    setShouldSearchTriggered(false)
  }, [condition, query, shouldSearchTriggered]);

  return (
    <div className={classes.root}>
      <SearchToolbar
        policyNumber={policyNumber}
        setPolicyNumber={setPolicyNumber}
        name={name}
        setName={setName}
        nameKana={nameKana}
        setNameKana={setNameKana}
        applyIdRef={applyIdRef}
        createdAtFrom={createdAtFrom}
        setCreatedAtFrom={setCreatedAtFrom}
        createdAtTo={createdAtTo}
        setCreatedAtTo={setCreatedAtTo}
        responseStatuses={responseStatuses}
        setResponseStatuses={setResponseStatuses}
        necessaryChangeTypes={necessaryChangeTypes}
        setNecessaryChangeTypes={setNecessaryChangeTypes}
        allowedChangeTypes={allowedChangeTypes}
        setAllowedChangeTypes={setAllowedChangeTypes}
        isIdentifiedAll={isIdentifiedAll}
        setIsIdentifiedAll={setIsIdentifiedAll}
        feeCategory={feeCategory}
        setFeeCategory={setFeeCategory}
        cancellationCategory={cancellationCategory}
        setCancellationCategory={setCancellationCategory}
        existsCallDesignated={existsCallDesignated}
        setExistsCallDesignated={setExistsCallDesignated}
        isSmsSendingRequired={isSmsSendingRequired}
        setIsSmsSendingRequired={setIsSmsSendingRequired}
        lineChatAvailability={lineChatAvailability}
        setLineChatAvailability={setLineChatAvailability}
        onClickSearch={onClickSearch}
        searchTabType={searchTabType}
      />
      <div className={classes.content}>
        <SearchTable
          applies={applies}
          loading={loading}
          totalCount={totalCount}
          query={query}
          setQuery={setQuery}
          onClickDetail={onClickDetail}
          onClickSearch={onClickSearch}
          setShouldSearchTriggered={setShouldSearchTriggered}
        />
      </div>
    </div>
  );
};

export default Search;
